import urls from '../../common/js/urls';
import {fetchData, getCookie} from '../../common/js/api';

// filter is 'all' or 'unread'; category is one of the page's categories, or null for all of them.
// withSummary asks for the counters of the page's category cards (the first page only): summary for the filter,
// and unread_summary.
export function listNotifications({lang, limit, before = null, filter = 'all', category = null, withSummary = false}) {
    const params = new URLSearchParams({lang, limit: String(limit), filter});
    if (before) {
        params.set('before', String(before));
    }
    if (category) {
        params.set('category', category);
    }
    if (withSummary) {
        params.set('with_summary', '1');
    }
    return fetchData({url: `${urls.inappNotifications}?${params.toString()}`});
}

// A count request still unanswered after this is aborted: the poll waits for it, so a hung one would stop polling.
export const UNREAD_COUNT_TIMEOUT_MS = 15 * 1000;

// Rejects on a non-OK status other than 401 and 403, and when the timeout aborts the request.
export async function fetchUnreadCount() {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), UNREAD_COUNT_TIMEOUT_MS);
    try {
        const response = await fetch(urls.inappNotificationsUnreadCount, {
            credentials: 'include',
            headers: {Accept: 'application/json'},
            signal: controller.signal,
        });
        if (response.status === 401 || response.status === 403) {
            return {signedOut: true};
        }
        if (!response.ok) {
            throw new Error(`Unread count failed with status ${response.status}`);
        }
        // Awaited here, so the timeout also covers the body.
        return await response.json();
    } finally {
        clearTimeout(timer);
    }
}

// Resolves to the response body ({unread_count}) and rejects on a non-OK status.
export async function markNotificationRead(id, {keepalive = false} = {}) {
    if (keepalive) {
        // fetchData has no keepalive; the request must outlive the navigation that follows a click.
        const response = await fetch(urls.inappNotificationRead(id), {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: {'X-CSRFToken': getCookie('csrftoken')},
        });
        if (!response.ok) {
            throw new Error(`Mark read failed with status ${response.status}`);
        }
        return response.json();
    }
    return fetchData({url: urls.inappNotificationRead(id), method: 'POST'});
}

// Without a category it marks every category, as the bell does.
export function markAllNotificationsRead(upToId, {category = null} = {}) {
    const body = {};
    if (upToId) {
        body.up_to_id = upToId;
    }
    if (category) {
        body.category = category;
    }
    return fetchData({
        url: urls.inappNotificationsReadAll,
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        data: JSON.stringify(body),
    });
}

// The unread popup announcements to open, oldest first ({items}); rejects on a non-OK status. exclude lists the ids of
// the popups this browser keeps closed: the server leaves them out before it applies its limit of 5, so they cannot
// hold back a newer one (it takes at most INAPP_POPUPS_MAX_EXCLUDED = 50 ids and refuses more).
export function listPopups(lang, exclude = []) {
    const params = new URLSearchParams({lang});
    if (exclude.length) {
        params.set('exclude', exclude.join(','));
    }
    return fetchData({url: `${urls.inappNotificationPopups}?${params.toString()}`});
}
